import { useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { useNavigate, useLocation } from 'react-router';
import logo from '../assets/logo/logo.svg';
import LoginForm from '../components/auth/LoginForm';
import SignupForm from '../components/auth/SignupForm';
import EmailVerificationFlow from '../components/auth/EmailVerificationFlow';
import PasswordResetFlow from '../components/auth/PasswordResetFlow';
import LanguageSwitcher from '../components/LanguageSwitcher';

type AuthMode = 'login' | 'signup' | 'verify-email' | 'reset-password';

const Auth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [verificationEmail, setVerificationEmail] = useState('');

  // Determine mode based on current path (no state needed!)
  const getMode = (): AuthMode => {
    const path = location.pathname;
    if (path === '/signup') return 'signup';
    if (path === '/reset-password') return 'reset-password';
    if (path === '/verify-email') return 'verify-email';
    return 'login';
  };

  const mode = getMode();

  const handleLoginSuccess = () => {
    navigate('/');
  };

  const handleSignupSuccess = (email: string) => {
    setVerificationEmail(email);
    navigate('/verify-email');
  };

  const handleVerificationSuccess = () => {
    navigate('/login');
  };

  const handlePasswordResetSuccess = () => {
    navigate('/login');
  };

  return (
    <Flex
      minHeight="100vh"
      width="100%"
      backgroundColor="neutral.900"
      justifyContent="center"
      alignItems="center"
      overflowY="auto"
      position="relative"
    >
      <Flex
        flexDirection="column"
        gap="28px"
        py="24px"
        px="16px"
      >
        {/* Logo */}
        <Flex
          width="100%"
          justifyContent="center"
          alignItems="center"
          gap="8px"
          cursor="pointer"
          onClick={() => navigate('/login')}
        >
          <img src={logo} alt="EasyQR" style={{ height: 20 }} />
        </Flex>

        {/* Form Card */}
        <Box
          width={{ base: "100%", md: "440px" }}
          pt={{ base: "0px", md: "24px" }}
          pb={{ base: "0px", md: "36px" }}
          px={{ base: "0px", md: "48px" }}
          borderRadius="8px"
          backgroundColor={{ base: "transparent", md: "neutral.800" }}
          borderWidth={{ base: "0px", md: "1px" }}
          borderColor="neutral.700"
        >
          {mode === 'login' && (
            <LoginForm
              onSuccess={handleLoginSuccess}
              onSwitchToSignup={() => navigate('/signup')}
              onSwitchToPasswordReset={() => navigate('/reset-password')}
            />
          )}

          {mode === 'signup' && (
            <SignupForm
              onSuccess={handleSignupSuccess}
              onSwitchToLogin={() => navigate('/login')}
            />
          )}

          {mode === 'verify-email' && (
            <EmailVerificationFlow
              email={verificationEmail}
              onSuccess={handleVerificationSuccess}
              onBack={() => navigate('/signup')}
            />
          )}

          {mode === 'reset-password' && (
            <PasswordResetFlow
              onSuccess={handlePasswordResetSuccess}
              onBack={() => navigate('/login')}
            />
          )}
        </Box>
      </Flex>

      {/* Language Switcher - bottom right */}
      <Box position="fixed" bottom="16px" right="16px">
        <LanguageSwitcher />
      </Box>
    </Flex>
  );
};

export default Auth;
